/**
 * ClubSettingsCalendarPage - Club iCal Feed Settings
 *
 * Allows club leaders to get their club's calendar feed URL
 * for subscribing in Google Calendar, Apple Calendar, or Outlook.
 *
 * Issue #1058: Club/team calendar ICS subscription feed
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOroMutation, OroOperation } from '../../hooks/useOroMutation';
import { TopBar, Button, Skeleton, ConfirmDialog, OroLoader } from '../../components';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useClub } from '../../hooks/useClubs';
import {
  getClubCalendarFeed,
  regenerateClubCalendarFeed,
  type ClubCalendarFeedResponse,
} from '../../lib/api/calendar';
import { Calendar, RefreshCw } from 'lucide-react';
import CalendarSubscribeActions from '../../components/CalendarSubscribeActions';

export default function ClubSettingsCalendarPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);

  const { data: clubData } = useClub(slug || '');
  const clubName = clubData?.club?.name || 'Club';

  useDocumentTitle(`${clubName} Calendar Feed`);

  // Fetch club calendar feed URL
  const { data, isLoading, error } = useQuery({
    queryKey: ['club-calendar-feed', slug],
    queryFn: () => getClubCalendarFeed(slug!),
    enabled: !!slug,
  });

  // Regenerate mutation
  const regenerateMutation = useOroMutation<ClubCalendarFeedResponse, void>({
    mutationFn: () => regenerateClubCalendarFeed(slug!),
    oro: { op: OroOperation.RegenerateClubCalendarFeed },
    successToast: 'Calendar feed URL regenerated',
    onSuccess: (newData) => {
      queryClient.setQueryData(['club-calendar-feed', slug], newData);
    },
  });

  const handleRegenerateClick = () => {
    setShowRegenerateConfirm(true);
  };

  const handleRegenerateConfirm = () => {
    setShowRegenerateConfirm(false);
    regenerateMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Calendar Feed" onBack={() => navigate(`/clubs/${slug}/settings`)} />
        <div className="flex-1 overflow-y-auto pt-14 p-4 space-y-6">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Calendar Feed" onBack={() => navigate(`/clubs/${slug}/settings`)} />
        <div className="flex-1 overflow-y-auto pt-14 p-4">
          <div className="bg-oro-error/10 border border-oro-error/20 rounded-xl p-4">
            <p className="text-oro-error">
              {(error as { error?: { message?: string } })?.error?.message ||
                'Failed to load calendar feed. Only club leaders can access this.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const feedUrl = data?.calendar_feed?.url || '';

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Calendar Feed" onBack={() => navigate(`/clubs/${slug}/settings`)} />

      <div className="flex-1 overflow-y-auto pt-14 p-4 space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto">
            <Calendar className="w-6 h-6 text-accent" />
          </div>
          <h1 className="text-xl font-semibold text-text-primary">{clubName} Calendar Feed</h1>
          <p className="text-text-secondary text-sm">
            Share this link with members to subscribe to {clubName} events
          </p>
        </div>

        {/* Subscribe Actions */}
        <CalendarSubscribeActions
          feedUrl={feedUrl}
          calendarName={`${clubName} Events`}
        />

        {/* Regenerate Token */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-text-primary">Regenerate URL</h3>
              <p className="text-xs text-text-muted mt-0.5">
                Invalidates the current URL and creates a new one
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRegenerateClick}
              disabled={regenerateMutation.isPending}
              className="flex items-center justify-center gap-2"
            >
              {regenerateMutation.isPending ? (
                <OroLoader variant="bounce" size={16} label="Regenerating" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Security Note */}
        <div className="bg-oro-surface-alt border border-oro-border rounded-xl p-4">
          <p className="text-text-muted text-sm">
            <strong className="text-text-secondary">Sharing note:</strong> Anyone with this URL can
            see your club's event schedule. Share it with trusted members. If the URL is shared
            unintentionally, use the regenerate button to create a new one (the old URL will stop
            working).
          </p>
        </div>
      </div>

      {/* Regenerate Confirmation */}
      <ConfirmDialog
        isOpen={showRegenerateConfirm}
        title="Regenerate Feed URL"
        message="This will invalidate the current feed URL. Anyone using the old URL will need to re-subscribe with the new one. Are you sure?"
        confirmLabel="Regenerate"
        cancelLabel="Cancel"
        variant="destructive"
        isPending={regenerateMutation.isPending}
        onConfirm={handleRegenerateConfirm}
        onCancel={() => setShowRegenerateConfirm(false)}
      />
    </div>
  );
}

